<template>
  <section
    class="overflow-hidden"
    :style="`width: ${currentSize}px; height: ${currentSize}px;`"
  >
    <svg
      aria-hidden="true"
      class="origin-top-left"
      :height="SIZES.DEFAULT"
      preserveAspectRatio="xMidYMid meet"
      :style="
        size === 'small'
          ? `transform: scale(${SIZES.SMALL / SIZES.DEFAULT});`
          : undefined
      "
      :viewBox="`0 0 ${SIZES.DEFAULT} ${SIZES.DEFAULT}`"
      :width="SIZES.DEFAULT"
    >
      <defs>
        <path
          :id="`base-shape-path-${id}`"
          d="M36.3127 12.986C39.9599 1.57544 54.103 -2.2142 62.9668 5.84412C65.31 7.97434 68.8109 8.16508 71.3552 6.30113C80.9797 -0.749906 94.6649 4.55588 97.1463 16.3004C97.8022 19.4051 100.53 21.6293 103.683 21.6298C115.609 21.6316 123.609 34.0062 118.76 44.9509C117.478 47.8441 118.391 51.2523 120.948 53.1171C130.619 60.171 129.878 74.8877 119.551 80.8521C116.821 82.4288 115.571 85.7191 116.555 88.7358C120.279 100.148 111.08 111.585 99.2192 110.291C96.0838 109.949 93.1473 111.865 92.1831 114.881C88.536 126.292 74.3929 130.081 65.5291 122.023C63.1859 119.893 59.685 119.702 57.1407 121.566C47.5162 128.617 33.831 123.311 31.3496 111.567C30.6937 108.462 27.9655 106.238 24.813 106.237C12.8873 106.235 4.88733 93.8608 9.73621 82.9161C11.018 80.0229 10.1048 76.6146 7.54809 74.7499C-2.12351 67.696 -1.38258 52.9793 8.94443 47.0149C11.6744 45.4382 12.9249 42.1479 11.9406 39.1312C8.21728 27.7194 17.4161 16.2819 29.2767 17.576C32.4121 17.9181 35.3486 16.0025 36.3127 12.986Z"
        />
        <clipPath :id="`clip-bg-${id}`" clipPathUnits="userSpaceOnUse">
          <use :href="`#base-shape-path-${id}`" />
        </clipPath>
        <!-- Fallback gradient for Safari -->
        <radialGradient
          :id="`fallback-gradient-${id}`"
          cx="0"
          cy="0"
          gradientTransform="translate(97 -54.5) rotate(103.473) scale(246.792)"
          gradientUnits="userSpaceOnUse"
          r="1"
        >
          <stop offset="0%" stop-color="#F4DEEF" />
          <stop offset="20%" stop-color="#E8D4F0" />
          <stop offset="35%" stop-color="#D9E7F7" />
          <stop offset="50%" stop-color="#E6F3F5" />
          <stop offset="65%" stop-color="#F8E6DC" />
          <stop offset="80%" stop-color="#D1F3ED" />
          <stop offset="100%" stop-color="#F5E8DC" />
        </radialGradient>
      </defs>

      <!-- Safari fallback: simple gradient fill -->

      <use
        v-if="isSafari"
        :fill="`url(#fallback-gradient-${id})`"
        :href="`#base-shape-path-${id}`"
      />

      <!-- Non-Safari: foreignObject with parallax background -->
      <g v-else :clip-path="`url(#clip-bg-${id})`">
        <foreignObject
          :height="SIZES.DEFAULT"
          :width="SIZES.DEFAULT"
          x="0"
          y="0"
        >
          <div
            class="bg-fixed bg-center bg-repeat [background-image:var(--bgImg)] [background-size:1536px_1024px] motion-reduce:[background-attachment:unset] md:[background-position:0_-100px]"
            :style="{
              width: `${SIZES.DEFAULT}px`,
              height: `${SIZES.DEFAULT}px`,
              '--bgImg': `url(${imagePath}), radial-gradient(circle at 50% 50%, #F4DEEF 0%, #E8D4F0 20%, #D9E7F7 35%, #E6F3F5 50%, #F8E6DC 65%, #D1F3ED 80%, #F5E8DC 100%)`,
            }"
          />
        </foreignObject>
      </g>

      <g>
        <path
          d="M36.3127 12.9861C39.9598 1.57545 54.1034 -2.21429 62.9672 5.84402C65.3102 7.97391 68.8105 8.16466 71.3547 6.30106C80.9792 -0.749979 94.6653 4.55579 97.1467 16.3003C97.8027 19.4049 100.531 21.6294 103.683 21.6299C115.609 21.6321 123.608 34.0059 118.759 44.9504C117.477 47.8436 118.391 51.2526 120.948 53.1173C130.619 60.1712 129.878 74.8868 119.552 80.8514C116.822 82.428 115.571 85.7186 116.556 88.7353C120.279 100.147 111.079 111.585 99.2186 110.291C96.0836 109.949 93.1476 111.864 92.1834 114.88C88.5363 126.291 74.3927 130.081 65.5289 122.022C63.1858 119.892 59.6847 119.702 57.1405 121.566C47.516 128.617 33.8308 123.311 31.3494 111.566C30.6934 108.462 27.9652 106.237 24.8127 106.237C12.8874 106.235 4.88752 93.8609 9.73592 82.9164C11.0177 80.0232 10.1043 76.6141 7.54759 74.7494C-2.12351 67.6954 -1.38232 52.9795 8.94437 47.0152C11.6743 45.4385 12.9247 42.1479 11.9405 39.1312C8.2172 27.7195 17.416 16.2818 29.2765 17.5759C32.4118 17.918 35.3485 16.0024 36.3127 12.9861ZM62.6306 6.21425C54.0362 -1.59918 40.3256 2.07456 36.7893 13.1384C35.7532 16.3798 32.5955 18.4405 29.2226 18.0726C17.7288 16.8185 8.80325 27.9037 12.4155 38.9756C13.4723 42.2147 12.131 45.7515 9.19469 47.4475C-0.816185 53.2293 -1.53953 67.5028 7.84283 74.3458C10.5896 76.3492 11.5705 80.01 10.1934 83.1184C5.48956 93.7357 13.2527 105.735 24.8133 105.737C28.2043 105.738 31.1342 108.13 31.8385 111.463C34.2462 122.858 47.5186 127.995 56.8454 121.163C59.5825 119.157 63.3475 119.363 65.8655 121.652C74.4599 129.466 88.1705 125.792 91.7068 114.728C92.743 111.487 95.9006 109.426 99.2735 109.794C110.767 111.048 119.692 99.963 116.08 88.8911C115.023 85.6519 116.365 82.115 119.301 80.419C129.312 74.6372 130.036 60.3637 120.653 53.5207C117.906 51.5173 116.926 47.8566 118.303 44.7482C123.006 34.1308 115.243 22.1313 103.683 22.1295C100.292 22.129 97.361 19.7369 96.6566 16.4033C94.249 5.00866 80.9775 -0.128784 71.6507 6.70399C68.9136 8.70919 65.1486 8.50317 62.6306 6.21425Z"
          data-stroke
          fill="currentColor"
          fill-opacity="0.4"
        ></path>
        <g data-check-icon>
          <path
            d="M71.9832 57.1239C72.3619 56.468 72.1371 55.6292 71.4812 55.2505C70.8252 54.8718 69.9865 55.0966 69.6078 55.7525L62.5355 68.002L57.0046 64.8087C56.3487 64.43 55.5099 64.6548 55.1312 65.3107C54.7525 65.9667 54.9772 66.8054 55.6332 67.1841L61.56 70.606C62.6532 71.2371 64.0512 70.8626 64.6823 69.7693L71.9832 57.1239Z"
            fill="currentColor"
          ></path>
          <path
            clip-rule="evenodd"
            d="M59.6527 46.6456C50.1418 49.1941 44.4976 58.9701 47.046 68.4811C49.5945 77.992 59.3705 83.6362 68.8815 81.0878C78.3924 78.5393 84.0366 68.7633 81.4882 59.2523C78.9397 49.7414 69.1637 44.0972 59.6527 46.6456ZM49.6954 67.7712C47.539 59.7235 52.3149 51.4514 60.3626 49.295C68.4103 47.1387 76.6824 51.9145 78.8388 59.9622C80.9952 68.01 76.2193 76.282 68.1716 78.4384C60.1238 80.5948 51.8518 75.8189 49.6954 67.7712Z"
            fill="currentColor"
            fill-rule="evenodd"
          ></path>
        </g>
        <g>
          <path
            id="top-text-path"
            :d="
              // reversed path for Firefox
              isFirefox
                ? 'M98.4707,64A34.4707,34.4707 0 0 029.5293,64'
                : 'M29.5293,64a34.4707,34.4707 0 0,1 68.9414,0'
            "
            fill="none"
            transform="rotate(-15 64 64)"
          ></path>
          <text
            :key="`top-text-${id}-${isFirefox}`"
            :class="textClasses"
            fill="currentColor"
            :lengthAdjust="isTopTextTooLong ? 'spacing' : ''"
            :textLength="SIZES.DEFAULT - 19"
          >
            <textPath
              href="#top-text-path"
              :side="isFirefox ? 'right' : 'left'"
              startOffset="50%"
              text-anchor="middle"
              :textLength="SIZES.DEFAULT - 19"
            >
              {{ topText }}
            </textPath>
          </text>
        </g>
        <g>
          <path
            id="bottom-text-path"
            d="M20.58 75.63a44.95 44.95 0 1 0 86.84-23.26 44.95 44.95 0 1 0-86.84 23.26"
            fill="none"
          ></path>
          <text
            :class="textClasses"
            fill="currentColor"
            :lengthAdjust="isBottomTextTooLong ? 'spacing' : ''"
            text-anchor="middle"
            :textLength="computedBottomTextLength"
          >
            <textPath href="#bottom-text-path" startOffset="71">
              {{ bottomText }}
            </textPath>
          </text>
        </g>
      </g>
    </svg>
  </section>
</template>

<script setup lang="ts">
import { toPublicPath } from '#imports'
import { computed, useId } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { isBrowser } from '@backmarket/utils/env/isBrowser'
import { tw } from '@ds/utils/tailwind'

import translations from './Sticker.translations'

const props = withDefaults(
  defineProps<{
    size?: 'default' | 'small'
  }>(),
  {
    size: 'default',
  },
)

const SIZES = {
  SMALL: 30,
  DEFAULT: 128,
} as const

const i18n = useI18n()
const id = useId()

const currentSize = computed(() => {
  return props.size === 'small' ? SIZES.SMALL : SIZES.DEFAULT
})

const isSafari = computed(() => {
  return (
    import.meta.client &&
    navigator.userAgent.includes('Safari') &&
    !navigator.userAgent.includes('Chrome')
  )
})

const isFirefox = computed(() => {
  return isBrowser() && navigator.userAgent.includes('Firefox')
})

const imagePath = toPublicPath('/img/product/verified-refurbished/holo.avif')

const topText = i18n(translations.topText)
const bottomText = i18n(translations.bottomText)

const { market } = useMarketplace()
// Adding special classes for some copies that are problematic in some countries
const isFR = market.countryCode === MarketCountryCode.FR
const isPT = market.countryCode === MarketCountryCode.PT

// Translation request says that the maximum length should be 16, but we're
// getting 17 in some cases.
const isTopTextLong = computed(() => topText.length > 13)
const isTopTextTooLong = computed(() => topText.length > 16)

const isBottomTextLong = computed(() => bottomText.length > 13)
const isBottomTextTooLong = computed(() => bottomText.length > 16)

const isTextLong = computed(() => isTopTextLong.value || isBottomTextLong.value)
const isTextTooLong = computed(
  () => isTopTextTooLong.value || isBottomTextTooLong.value,
)

// TODO: Solve by modPDP with props
const textClasses = computed(() =>
  // eslint-disable-next-line no-nested-ternary
  isTextTooLong.value
    ? tw`caption-bold`
    : isTextLong.value
      ? tw`body-2-bold`
      : tw`body-1-bold`,
)

// TODO: Solve by modPDP with props
const computedBottomTextLength = computed(() => {
  // eslint-disable-next-line no-nested-ternary
  return isPT
    ? SIZES.DEFAULT - 45
    : isFR
      ? SIZES.DEFAULT - 40
      : SIZES.DEFAULT - 20
})
</script>
